// Header Login/Account control + auth modal.
//
// - Supabase not configured  -> renders nothing (app looks exactly as before)
// - Logged out               -> "LOG IN" button opening a Login/Sign-up modal
// - Logged in                -> account chip opening a small menu with Log out
//
// The modal is rendered through a portal into document.body: the app header
// uses backdrop-filter, which turns it into a containing block for
// position:fixed descendants — rendered in place, the overlay would be
// anchored to the header instead of the viewport (modal stuck at the top and
// cut off). The portal keeps the overlay a true full-viewport layer.
//
// All modules keep working without login; this is purely additive UI.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from './AuthContext';
import { onOpenAuth } from '../lib/uiBus';
import { accountLabel } from '../lib/authLogic';
import { useMyPlan } from '../lib/useMyPlan';
import { BRAND } from '../brand';
import Login from './Login';
import SignUp from './SignUp';
import LogOut from './LogOut';
import FullPageOverlay from '../home/FullPageOverlay';
import MyHistory from '../history/MyHistory';
import AdminDashboard from '../admin/AdminDashboard';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(26,31,54,0.45)',
  zIndex: 2000, // above the sticky header (z-index 50) and everything else
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px 16px calc(16px + env(safe-area-inset-bottom))',
};

const cardStyle = {
  boxSizing: 'border-box', // ~400px is the real total width, padding included
  backgroundColor: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 14,
  padding: 24,
  width: '90%',
  maxWidth: 400,
  maxHeight: '90vh', // on small screens the panel scrolls instead of clipping
  overflowY: 'auto',
};

const titleStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--amber)',
  fontFamily: 'var(--mono)',
  letterSpacing: 1,
  marginBottom: 16,
};

// Full-viewport modal: portaled to document.body, backdrop click closes,
// body scroll locked while open.
function AuthModal({ onClose, children }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div style={overlayStyle} onClick={onClose} role="dialog" aria-modal="true">
      <div style={cardStyle} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
}

export default function AuthButton() {
  const { user, loading, isConfigured } = useAuth();
  const { isAdmin } = useMyPlan();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('login'); // 'login' | 'signup'
  // Full-page sections launched from the account menu (Phase 4).
  const [section, setSection] = useState(null); // 'history' | 'admin' | null

  // Other UI (e.g. the home prompt bar) can ask the login modal to open.
  useEffect(() => onOpenAuth(() => setOpen(true)), []);

  if (!isConfigured || loading) return null;

  function close() {
    setOpen(false);
    setTab('login');
  }

  function openSection(name) {
    close();
    setSection(name);
  }

  return (
    <>
      <button
        className="header-chip account-chip"
        onClick={() => setOpen(true)}
        aria-label={user ? 'Account' : 'Log in'}
      >
        {user ? `${BRAND.logo} ${accountLabel(user)}` : 'LOG IN'}
      </button>

      {open && (
        <AuthModal onClose={close}>
          {user ? (
            <>
              <div style={titleStyle}>{BRAND.logo} YOUR ACCOUNT</div>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--text-2)',
                  marginBottom: 18,
                  wordBreak: 'break-all',
                }}
              >
                Signed in as <strong style={{ color: 'var(--text)' }}>{user.email}</strong>
              </div>
              <button
                className="btn btn-ghost"
                onClick={() => openSection('history')}
                style={{ width: '100%', marginBottom: 8 }}
              >
                🕘 My History
              </button>
              {isAdmin && (
                <button
                  className="btn btn-ghost"
                  onClick={() => openSection('admin')}
                  style={{ width: '100%', marginBottom: 8 }}
                >
                  🛠 Admin Dashboard
                </button>
              )}
              <LogOut onDone={close} />
              <button
                className="btn btn-ghost"
                onClick={close}
                style={{ width: '100%', marginTop: 8 }}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <div style={titleStyle}>
                {BRAND.logo} {BRAND.name.toUpperCase()} ACCOUNT
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                {[
                  ['login', 'Log in'],
                  ['signup', 'Sign up'],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className="btn btn-ghost"
                    style={{
                      flex: 1,
                      padding: '9px 0',
                      fontSize: 12.5,
                      ...(tab === key
                        ? {
                            borderColor: 'var(--amber)',
                            color: 'var(--amber)',
                            background: 'rgba(245,158,11,0.1)',
                          }
                        : {}),
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {tab === 'login' ? <Login onSuccess={close} /> : <SignUp />}
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-3)',
                  marginTop: 14,
                  lineHeight: 1.5,
                  textAlign: 'center',
                }}
              >
                You can keep using {BRAND.name} without an account.
              </div>
              <button
                className="btn btn-ghost"
                onClick={close}
                style={{ width: '100%', marginTop: 10 }}
              >
                Close
              </button>
            </>
          )}
        </AuthModal>
      )}

      {section === 'history' && (
        <FullPageOverlay label="My History">
          <MyHistory onBack={() => setSection(null)} />
        </FullPageOverlay>
      )}
      {section === 'admin' && (
        <FullPageOverlay label="Admin Dashboard">
          <AdminDashboard onBack={() => setSection(null)} />
        </FullPageOverlay>
      )}
    </>
  );
}
